import { telemetry } from '../../../@vendor/Posthog';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { TaskData } from '../../../api/endpoints/tasks';
import { uuid, waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { BoardInfo } from '../../canvas/BoardInfo';
import { resolvePhysical, resolveSubject, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { place } from '../../canvas/commands/OrderCommands';
import { select } from '../../canvas/commands/SelectionCommands';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { importTasksPopup } from '../../dashboard/tasks/ImportTasksPopup';
import { busyPopup } from '../../popups/BusyPopup';
import { PopupManager } from '../../popups/PopupManager';
import { ProfileModel } from '../../ProfileModel';
import { AutoLayout } from '../autoLayout/AutoLayout';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { FlexLayout } from '../autoLayout/FlexLayout';
import { Sticky } from '../paper/Sticky';
import { Assignable } from './Assignable';
import { TaskRecord, TaskRecordAttr } from './TaskAssocations';
import { TaskRecordManager } from './TaskRecordManager';
import { AssignmentWidget } from './TaskWidget';

const cleanTaskSummary = (content: string) => {
    // Trim, and if has a trailing atsert denotation (@ or #), remove it
    content = content.trim();
    content = content.match(/[#@]$/) ? content.slice(0, -1) : content;
    return content;
};

export const tasksLoadMissing = defineCommand(
    'tasksLoadMissing',
    null,
    async (cc) => {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const allAssignables = Array.from(objects.getAll(Assignable));
        const board = cc.resolve(EditorSession).get(BoardInfo);

        const recordSet = cc.resolve(RecordSetManager).resolve<TaskData>('Tasks');
        const missing = allAssignables.filter(x => !!x.taskId && (recordSet.get(x.taskId)?.future ?? true));

        if (!!missing.length) {
            await api.exec(v2.tasks.load({
                boardId: board.id,
                taskIds: missing.map(x => x.taskId),
            }));
        }
    }
);

export const tasksLoadInitial = defineCommand(
    'tasksLoadInitial',
    null,
    async cc => {

        const me = cc.resolve(ProfileModel);
        const ptpts = cc.resolve(ParticipantManager);

        // Only if we are the first online
        if (ptpts.online().filter(x => x.id !== me.id).length === 0) {
            await cc.dispatch(tasksLoad);
        }
    }
);

export const tasksLoad = defineCommand(
    'tasksLoad',
    null,
    async (cc) => {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const allAssignables = Array.from(objects.getAll(Assignable));
        const board = cc.resolve(EditorSession).get(BoardInfo);

        const taskIds = allAssignables.map(x => x.taskId).filter(x => !!x);
        if (!!taskIds.length) {
            await api.exec(v2.tasks.load({
                boardId: board.id,
                taskIds,
            }));
        }
    }
);

export const tasksImport = defineCommand(
    'tasksImport',
    'Import: Action Items...',
    async (cc, [container]: [any]) => {

        telemetry.emit('actions.beginImport');

        const objects = cc.resolve(ObjectManager);
        const popups = cc.resolve(PopupManager);
        const tasks = cc.resolve(TaskRecordManager);

        const { data } = await popups.invoke(importTasksPopup());
        const items = data as TaskRecord[] | undefined;
        if (!items?.length) return;

        await popups.invoke(busyPopup(
            async () => {

                const futures = items.map(x => tasks.load(x.id));
                await Promise.all(futures);

                const key = uuid();

                const stickies = objects.update(u => {
                    u.set('key', `actionImport:${key}`);
                    return items.map(tm =>
                        Physicals.createAtCameraPosition(cc, Sticky, nc => {
                            nc.taskId = tm.id;
                            nc.content = tm.summary;
                        })
                    );
                });

                // Wait for binding manager to have done it's magic
                await waitFor(10);

                objects.update(u => {
                    u.set('key', `actionImport:${key}`);

                    const cont = resolvePhysical(cc, container);
                    if (!!cont) {
                        FlexLayout.exec(cont.bounds(RelativeTo.Model), ['row', 'start', 'start', 10], stickies);
                        cc.dispatch(place, [stickies.map(x => x.id)]);
                        if (cont.is(AutoLayout)) {
                            cc.dispatch(autoLayout, [cont.id]);
                        }
                    }
                    else {
                        // Add 10 for spacing
                        const mw = Math.max(...stickies.map(x => x.width)) + 10;
                        const mh = Math.max(...stickies.map(x => x.height)) + 10;

                        const w = mw * Math.ceil(Math.sqrt(stickies.length));
                        const h = mh * Math.ceil(Math.sqrt(stickies.length));
                        const area = Rect.fromCenter(stickies[0].center(RelativeTo.Model), new Point(w, h).divide(2));
                        FlexLayout.exec(area, ['row', 'start', 'start', 10], stickies);
                    }
                });

                cc.dispatch(select, [stickies.map(x => x.id)]);

            }
        ));

        telemetry.emit('actions.completeImport', { count: items?.length || 0 });

    },
    [
        stringStep('containerId', 'Container:')
    ]
);

export const tasksAssign = defineCommand(
    'tasksAssign',
    'Tasks: Assign',
    async (cc, [userId, dueDate, targetIds]: [string, string, string[]?]) => {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const tasks = cc.resolve(TaskRecordManager);
        const board = cc.resolve(EditorSession).get(BoardInfo);
        const subjects = resolveSubjects(cc, targetIds, Assignable);
        if (!subjects.length) return;

        const toPatch = subjects.filter(x => !!x.taskId);
        tasks.patch({ userId, dueDate, status: 'open' }, toPatch.map(x => x.id));

        const toCreate = subjects.filter(x => !x.taskId);
        if (!toCreate.length) return;

        const items = toCreate.map(x => ({
            localId: x.id,
            userId,
            dueDate,
            status: 'open',
            summary: cleanTaskSummary(x.toText()),
        }));

        const { mappings } = await api.exec(v2.tasks.create({
            boardId: board.id,
            items,
        }));

        objects.update(() => {
            for (const { localId } of items) {
                const taskId = mappings[localId];
                if (!taskId) continue;
                const sb = objects.getMaybe(localId, Assignable);
                if (!sb) continue;
                sb.taskId = taskId;
            }
        });
    }
);

export const taskDelete = defineCommand(
    'taskDelete',
    'Task: Delete',
    (cc, [targetIds]: [string[]?]) => {

        const objects = cc.resolve(ObjectManager);
        const tasks = cc.resolve(TaskRecordManager);

        const subjects = resolveSubjects(cc, targetIds, Assignable);
        if (!subjects.length) return;

        objects.update(() => {
            for (const sb of subjects) {
                sb.taskId = null;
            }
        });

        tasks.patch({ status: 'deleted' }, targetIds);
    }
);

export const taskSetAssignee = defineCommand(
    'taskSetAssignee',
    'Task: Set Assignee',
    (cc, [userId, targetIds]: [string, string[]?]) => {

        const tasks = cc.resolve(TaskRecordManager);
        tasks.patch({ userId }, targetIds);
    }
);

export const taskSetDueDate = defineCommand(
    'taskSetDueDate',
    'Task: Set Due Date',
    (cc, [dueDate, targetIds]: [string, string[]?]) => {

        const tasks = cc.resolve(TaskRecordManager);
        tasks.patch({ dueDate }, targetIds);
    }
);

export const taskSetStatus = defineCommand(
    'taskSetStatus',
    'Task: Set Status',
    (cc, [status, targetIds]: [string, string[]?]) => {

        const tasks = cc.resolve(TaskRecordManager);
        tasks.patch({ status }, targetIds);
    }
);

export const taskSetSummary = defineCommand(
    'taskSetSummary',
    'Task: Set Summary',
    (cc, [summary, targetIds]: [string, string[]?]) => {

        const tasks = cc.resolve(TaskRecordManager);
        tasks.patch({ summary }, targetIds);
    }
);

export const taskToggleStatus = defineCommand(
    'taskToggleStatus',
    'Task: Toggle Status',
    (cc, [targetIds]: [string[]?]) => {

        const effects = cc.call(EffectDirector);
        const subjects = resolveSubjects(cc, targetIds, Assignable);

        const [first] = subjects.map(x => TaskRecordAttr.get(x)).filter(x => !!x);

        const status = first?.status !== 'open' ? 'open' : 'done';
        cc.dispatch(taskSetStatus, [status, targetIds]);

        if (status === 'done') {
            const rect = (subjects[0] as any)?.layout?.task?.frame?.pointAt(.5, 0).add([0, -10]);
            if (rect) {
                effects.trigger(Effects.text('DONE', rect, Colors.positive), subjects[0]);
                effects.send(Effects.text('DONE', rect, Colors.positive), subjects[0]);
            }
        }
    }
);

export const taskOpen = defineCommand(
    'task',
    'Task: Open',
    (r, [targetId, toEdit]: [string?, boolean?]) => {
        toEdit = !!toEdit;

        const widgets = r.call(WidgetManager);
        const subject = resolveSubject(r, targetId, Physical);
        if (!subject) return false;

        widgets.delete('task');

        const cont = widgets.createContainer({
            name: 'task',
            position: ['attached', { to: subject.id, placement: 'bottom-start', offset: [.5, 0], zOrder: 111 }],
            direction: 'row',
            style: 'none',
        });

        cont.add(widgets.createInline({ ui: AssignmentWidget, subject, autoFocus: toEdit }));
        widgets.add(cont);
        return true;
    }
);

export const taskClose = defineCommand(
    'taskClose',
    'Task: Close',
    cc => {
        const widgets = cc.call(WidgetManager);
        widgets.delete('task');
    }
);

export const inTaskCommands = () => inCommands(
    tasksAssign,
    tasksImport,
    taskSetStatus,
    taskToggleStatus,
    taskDelete,
);
